import { type ApiContainerStockEntry, type ApiContainerStockGroup, type LinkableField } from '@Model';

/** One standing time as the stock lists it, with the key the table needs. */
export interface ContainerStockRow extends ApiContainerStockEntry {
    id: string;
}

/**
 * The standing times of one customer, site and container type, shown as a single line. The server builds it, the
 * lines only get the key the table needs.
 */
export interface ContainerStockGroup extends Omit<ApiContainerStockGroup, 'lines'> {
    lines: ContainerStockRow[];
}

const NOT_LINKED: LinkableField = { foreign_key: undefined, display_string: '', api_resource_name: '' };

/** The stock entries, keyed on their standing time. */
// @ai-generated 2026-10-02T08:10:14+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function toRows(entries: readonly ApiContainerStockEntry[]): ContainerStockRow[] {
    return entries.map((entry) => ({ ...entry, id: entry.standing_time_id }));
}

/** The groups the server built, with their lines keyed on their standing time. */
// @ai-generated 2026-10-22T12:00:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function toStockGroups(groups: readonly ApiContainerStockGroup[]): ContainerStockGroup[] {
    return groups.map((group) => ({ ...group, lines: toRows(group.lines) }));
}

/**
 * Groups the rows by customer, site and container type, and by content too when asked, in the order their first row
 * appears.
 */
// @ai-generated 2026-10-02T08:10:14+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function groupStock(rows: readonly ContainerStockRow[], byContent = false): ContainerStockGroup[] {
    const byKey = new Map<string, ContainerStockRow[]>();

    for (const row of rows) {
        const key = [
            row.customer.foreign_key,
            row.site.foreign_key,
            row.container_type.foreign_key,
            ...(byContent ? [row.content.foreign_key] : []),
        ].join('|');
        byKey.set(key, [...(byKey.get(key) ?? []), row]);
    }

    return [...byKey.entries()].map(([key, lines]) => toGroup(key, lines));
}

// @ai-generated 2026-10-02T08:10:14+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
function toGroup(id: string, rows: ContainerStockRow[]): ContainerStockGroup {
    const lines = [...rows].sort(
        (left, right) =>
            left.standing_since.localeCompare(right.standing_since) ||
            left.container.display_string.localeCompare(right.container.display_string, undefined, { numeric: true }),
    );
    const first = lines[0];

    return {
        id,
        container_type: first.container_type,
        customer: first.customer,
        site: first.site,
        container: { ...NOT_LINKED, display_string: listed(lines.map((line) => line.container.display_string)) },
        content: { ...NOT_LINKED, display_string: listed(lines.map((line) => line.content.display_string)) },
        remarks: shared(lines.map((line) => line.remarks)),
        count: lines.length,
        standing_since: first.standing_since,
        lines,
    };
}

/** What the values name, each once and comma separated; empty where none names anything. */
function listed(values: readonly string[]): string {
    return [...new Set(values.filter((value) => value !== ''))].join(', ');
}

/** The value every one of them has, or an empty one where they differ. */
function shared(values: readonly string[]): string {
    return values.every((value) => value === values[0]) ? values[0] : '';
}

/** Whether a group has no more than one line, so there is nothing to expand into. */
export const hasNoLinesToExpand = (group: { lines: readonly unknown[] }): boolean => group.lines.length < 2;
